import type { AnalyticsSender, Offer } from '@funtech-int/yango-shared';

import {
    channelPageSubscriptionOfferNavigatedV2,
    channelPageSubscriptionOfferShowedV2,
    channelPageSubscriptionOfferSucceedV2,
} from '@kinopoisk-int/evgen-web-events/web/channel_page';
import { ChannelPageOfferEntityTypes } from '@kinopoisk-int/evgen-web-events/web/named_enums';

import {
    OfferPlacementAnalytics,
    TARIFFICATOR_BILLING_PRODUCT_ID,
} from '@/shared/lib/analytics/offer';

import { transformOfferText } from './utils/transformOfferText';

interface ChannelOfferData {
    offer: Offer | null | undefined;
    buttonText: string;
    channelName: string;
    channelId: string;
}

type ChannelOfferAnalytics = OfferPlacementAnalytics<ChannelOfferData>;

export function createChannelOfferAnalytics(eventSender: AnalyticsSender): ChannelOfferAnalytics {
    let data: ChannelOfferData | undefined;

    const getEventOfferMeta = ({ offer, buttonText, channelName, channelId }: ChannelOfferData) => {
        const catalogCompositeOffer = offer?.catalogCompositeOffer;
        const tariffOffer = catalogCompositeOffer?.tariffOffer;
        const optionOffers = catalogCompositeOffer?.optionOffers || [];
        const firstOptionOffer = optionOffers?.[0];

        const offerTexts = offer?.offerTexts;

        const tariffName = tariffOffer?.tariff?.name;
        const optionName = firstOptionOffer?.option?.name;

        const text = buttonText ?? offerTexts?.buttonText;

        return {
            entityType: ChannelPageOfferEntityTypes.SubscriptionOption,
            offerTitle: transformOfferText({
                offerText: text,
                additionalText: offerTexts?.buttonTextWithDetails,
            }),
            buttonText: text,
            billingProductId: TARIFFICATOR_BILLING_PRODUCT_ID,
            billingProductIds: offer?.billingProductIds || 'NONE',
            offerTariffName: tariffName || 'NONE',
            offerOptionNames: optionName || 'NONE',
            position: 1,
            title: channelName || '',
            uuid: channelId,
        };
    };

    return {
        setData(value) {
            data = value;
        },
        showed() {
            if (!data) return;

            const { offer, channelId } = data;

            eventSender.sendOnce(channelPageSubscriptionOfferShowedV2, {
                ...getEventOfferMeta(data),
                uuid: channelId ?? '-',
                show: offer?.offerPositionsData ?? {},
            });
        },
        navigated() {
            if (!data) return;

            const { offer, channelId } = data;

            eventSender.send(channelPageSubscriptionOfferNavigatedV2, {
                ...getEventOfferMeta(data),
                uuid: channelId ?? '',
                show: offer?.offerPositionsData ?? {},
                click: offer?.offerPositionsData ?? {},
            });
        },
        succeeded() {
            if (!data) return;

            const { offer, channelId } = data;

            eventSender.send(channelPageSubscriptionOfferSucceedV2, {
                ...getEventOfferMeta(data),
                uuid: channelId ?? '',
                show: offer?.offerPositionsData ?? {},
            });
        },
    };
}
